import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck, Search, Phone, Mail, MapPin, ChevronRight,
  AlertTriangle, CheckCircle, FileText, Euro, Briefcase,
  Shield, Clock, ExternalLink
} from 'lucide-react';
import {
  sousTraitants, getAffaireById, getClientById
} from '../data/mockData';

const SousTraitants = () => {
  const [search, setSearch] = useState('');
  const [selectedST, setSelectedST] = useState(null);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const filteredST = sousTraitants.filter(st =>
    st.nom.toLowerCase().includes(search.toLowerCase()) ||
    st.specialite.toLowerCase().includes(search.toLowerCase()) ||
    st.contact.toLowerCase().includes(search.toLowerCase())
  );

  const totalST = sousTraitants.reduce((acc, st) =>
    acc + st.affaires.reduce((s, a) => s + a.montantDevis, 0), 0
  );

  const totalPaye = sousTraitants.reduce((acc, st) =>
    acc + st.affaires.filter(a => a.datePaiement).reduce((s, a) => s + a.montantTTC, 0), 0
  );

  const totalAPayer = sousTraitants.reduce((acc, st) =>
    acc + st.affaires.filter(a => !a.datePaiement && a.factureNumero).reduce((s, a) => s + a.montantTTC, 0), 0
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><UserCheck size={28} /> Sous-traitants</h1>
          <div className="page-meta">
            <span>{sousTraitants.length} sous-traitants</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><UserCheck size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Sous-traitants</p>
            <p className="stat-value">{sousTraitants.length}</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><Euro size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Montant total engagé</p>
            <p className="stat-value">{formatMontant(totalST)}</p>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Payé</p>
            <p className="stat-value">{formatMontant(totalPaye)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}><Clock size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">À payer</p>
            <p className="stat-value">{formatMontant(totalAPayer)}</p>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom, spécialité, contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Liste des sous-traitants */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedST ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredST.map(st => {
            const totalEngageST = st.affaires.reduce((acc, a) => acc + a.montantDevis, 0);
            const isSelected = selectedST?.id === st.id;

            return (
              <div
                key={st.id}
                onClick={() => setSelectedST(isSelected ? null : st)}
                style={{
                  background: 'white', borderRadius: 'var(--radius-lg)',
                  border: isSelected ? '2px solid var(--primary)' : '1px solid var(--gray-200)',
                  cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: isSelected ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : undefined
                }}
              >
                <div style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.0625rem' }}>{st.nom}</h3>
                        <span className={`badge ${st.documentsValides ? 'badge-success' : 'badge-danger'}`}>
                          <Shield size={10} />
                          {st.documentsValides ? 'Documents OK' : 'Documents manquants'}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--primary)', fontWeight: 500 }}>
                        {st.specialite}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.0625rem' }}>{formatMontant(totalEngageST)}</div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{st.affaires.length} affaire{st.affaires.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <UserCheck size={14} style={{ color: 'var(--gray-400)' }} /> {st.contact}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Phone size={14} style={{ color: 'var(--gray-400)' }} /> {st.telephone}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Mail size={14} style={{ color: 'var(--gray-400)' }} /> {st.email}
                    </span>
                  </div>
                </div>

                {!st.documentsValides && (
                  <div style={{
                    padding: '0.5rem 1.25rem', background: '#FEF2F2', borderTop: '1px solid #FECACA',
                    display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--danger)'
                  }}>
                    <AlertTriangle size={14} />
                    Documents de vérification manquants — à demander
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Détail du sous-traitant sélectionné */}
        {selectedST && (
          <div style={{
            background: 'white', borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--gray-200)', position: 'sticky', top: '1rem',
            alignSelf: 'flex-start'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)' }}>
              <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>{selectedST.nom}</h2>
              <p style={{ margin: 0, color: 'var(--primary)', fontWeight: 500 }}>{selectedST.specialite}</p>
            </div>

            <div style={{ padding: '1.25rem' }}>
              <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                Informations de contact
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <UserCheck size={14} style={{ color: 'var(--gray-400)' }} /> {selectedST.contact}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <Phone size={14} style={{ color: 'var(--gray-400)' }} /> {selectedST.telephone}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <Mail size={14} style={{ color: 'var(--gray-400)' }} /> {selectedST.email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                  <MapPin size={14} style={{ color: 'var(--gray-400)' }} /> {selectedST.adresse}
                </div>
              </div>

              <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                Documents de vérification
              </h4>
              <div style={{ marginBottom: '1.25rem' }}>
                {selectedST.documentsValides ? (
                  <div style={{ padding: '0.75rem', background: '#D1FAE5', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#059669' }}>
                    <CheckCircle size={16} /> Tous les documents sont à jour
                  </div>
                ) : (
                  <div style={{ padding: '0.75rem', background: '#FEE2E2', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#DC2626', marginBottom: '0.5rem' }}>
                      <AlertTriangle size={16} /> Documents manquants
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8125rem', color: 'var(--gray-700)' }}>
                      <li>Attestation URSSAF</li>
                      <li>Attestation d'assurance RC Pro</li>
                      <li>Extrait Kbis à jour</li>
                    </ul>
                  </div>
                )}
              </div>

              <h4 style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                Affaires ({selectedST.affaires.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {selectedST.affaires.map((stAffaire, index) => {
                  const affaire = getAffaireById(stAffaire.affaireId);
                  const client = affaire ? getClientById(affaire.clientId) : null;
                  return (
                    <div key={index} style={{
                      padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                      border: '1px solid var(--gray-200)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <Link to={`/affaires/${stAffaire.affaireId}`} style={{
                          fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)', textDecoration: 'none',
                          display: 'flex', alignItems: 'center', gap: '0.25rem'
                        }}>
                          {affaire?.numero} <ExternalLink size={12} />
                        </Link>
                        <span style={{ fontWeight: 600 }}>{formatMontant(stAffaire.montantDevis)}</span>
                      </div>
                      <p style={{ margin: '0 0 0.25rem', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                        {client?.nom} — {affaire?.designation}
                      </p>
                      {stAffaire.factureNumero && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--gray-200)' }}>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                            Facture : <strong>{stAffaire.factureNumero}</strong>
                          </span>
                          {stAffaire.datePaiement ? (
                            <span className="badge badge-success">
                              <CheckCircle size={10} /> Payé le {new Date(stAffaire.datePaiement).toLocaleDateString('fr-FR')}
                            </span>
                          ) : (
                            <span className="badge badge-warning">
                              <Clock size={10} /> {formatMontant(stAffaire.montantTTC)} TTC à payer
                            </span>
                          )}
                        </div>
                      )}
                      {!stAffaire.factureNumero && (
                        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--gray-200)' }}>
                          <span className="badge badge-secondary">En attente de facture</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {selectedST.affaires.length === 0 && (
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Aucune affaire en cours</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SousTraitants;
